import React from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ClipboardList, Sparkles, MessageCircle, Handshake, ArrowRight, ShieldCheck, Clock,
  Wallet, CheckCircle2, Search, Star,
} from "lucide-react";

const ZoWerktHet = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-50/60 via-white to-white" />
          <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#F5A623]/15 blur-3xl" />
        </div>
        <div className="max-w-5xl mx-auto px-4 md:px-8 pt-14 md:pt-20 pb-12">
          <span className="pill-amber">{t("zwh.badge")}</span>
          <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-[#1A2B4A] mt-4 leading-[1.05] tracking-tight">
            {t("zwh.title_1")} <span className="text-[#F5A623]">{t("zwh.title_highlight")}</span>{t("zwh.title_2")}
          </h1>
          <p className="text-lg text-slate-600 mt-5 max-w-2xl">{t("zwh.subtitle")}</p>
          <div className="mt-7 flex flex-wrap gap-4 items-center text-sm text-slate-700">
            <span className="flex items-center gap-2"><Clock size={16} className="text-[#F5A623]" /> {t("zwh.minute")}</span>
            <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#22C55E]" /> {t("zwh.verified")}</span>
            <span className="flex items-center gap-2"><Wallet size={16} className="text-[#22C55E]" /> {t("zwh.no_match")}</span>
          </div>
        </div>
      </section>

      {/* 4 STEP ZIGZAG */}
      <section className="bg-[#F8FAFC] py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 md:px-8 space-y-12 md:space-y-20">
          {[
            { n: "01", icon: ClipboardList, title: t("zwh.s1_title"), desc: t("zwh.s1_desc"), bullets: [t("zwh.s1_b1"), t("zwh.s1_b2"), t("zwh.s1_b3")] },
            { n: "02", icon: Sparkles, title: t("zwh.s2_title"), desc: t("zwh.s2_desc"), bullets: [t("zwh.s2_b1"), t("zwh.s2_b2"), t("zwh.s2_b3")] },
            { n: "03", icon: MessageCircle, title: t("zwh.s3_title"), desc: t("zwh.s3_desc"), bullets: [t("zwh.s3_b1"), t("zwh.s3_b2"), t("zwh.s3_b3")] },
            { n: "04", icon: Handshake, title: t("zwh.s4_title"), desc: t("zwh.s4_desc"), bullets: [t("zwh.s4_b1"), t("zwh.s4_b2"), t("zwh.s4_b3")] },
          ].map((s, idx) => {
            const Icon = s.icon;
            const reverse = idx % 2 === 1;
            return (
              <div key={s.n} className={`grid md:grid-cols-2 gap-10 items-center ${reverse ? "md:[&>div:first-child]:order-2" : ""}`} data-testid={`step-${s.n}`}>
                <div>
                  <div className="inline-flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-[#F5A623] text-white flex items-center justify-center font-heading font-extrabold text-xl">
                      {s.n}
                    </div>
                    <Icon size={26} className="text-[#1A2B4A]" />
                  </div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A2B4A] mt-4 tracking-tight">{s.title}</h2>
                  <p className="text-slate-600 mt-3">{s.desc}</p>
                  <ul className="mt-5 space-y-2.5">
                    {s.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 size={16} className="text-[#22C55E] flex-shrink-0 mt-0.5" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="beecenter-card p-8 bg-white">
                  <div className="aspect-[5/4] rounded-2xl overflow-hidden bg-gradient-to-br from-amber-50 via-white to-slate-50 flex items-center justify-center">
                    <Icon size={120} className="text-[#F5A623] opacity-25" strokeWidth={1.4} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F8FAFC] py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2B4A] tracking-tight">{t("zwh.faq_title")}</h2>
          <div className="mt-8 space-y-3">
            {[
              { q: t("zwh.faq_q1"), a: t("zwh.faq_a1") },
              { q: t("zwh.faq_q2"), a: t("zwh.faq_a2") },
              { q: t("zwh.faq_q3"), a: t("zwh.faq_a3") },
              { q: t("zwh.faq_q4"), a: t("zwh.faq_a4") },
              { q: t("zwh.faq_q5"), a: t("zwh.faq_a5") },
            ].map((f, i) => (
              <details key={i} className="beecenter-card group" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer list-none p-5 flex items-center justify-between gap-4">
                  <span className="font-heading font-semibold text-[#1A2B4A]">{f.q}</span>
                  <span className="w-8 h-8 rounded-full bg-slate-100 group-hover:bg-[#F5A623]/10 flex items-center justify-center text-slate-500 group-open:rotate-45 transition-all">+</span>
                </summary>
                <div className="px-5 pb-5 -mt-2 text-sm text-slate-600 leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1A2B4A] text-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight">{t("zwh.cta_title")}</h2>
          <p className="text-slate-300 mt-4 text-lg">{t("zwh.cta_subtitle")}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/onboarding" className="btn-amber" data-testid="zwh-cta-start">
              <Search size={16} /> {t("zwh.cta_btn")}
            </Link>
            <Link to="/diensten" className="btn-outline-navy !text-white !border-white hover:!bg-white hover:!text-[#1A2B4A]" data-testid="zwh-cta-services">
              {t("services.view_all")}
            </Link>
          </div>
          <div className="mt-10 flex items-center justify-center gap-2 text-sm text-slate-300">
            <div className="flex">{[1,2,3,4,5].map((s) => <Star key={s} size={14} className="fill-[#F5A623] text-[#F5A623]" />)}</div>
            <span className="font-semibold text-white">4.9/5</span>
            <span>·</span>
            <span>{t("home.hero.reviews_count")}</span>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ZoWerktHet;
